import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosInstance';
import colors from '../../constants/colors';

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    isAdmin: false,
    isBanned: false,
    isVerified: true
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validated, setValidated] = useState(false);
  
  useEffect(() => {
    // Check if user is admin
    if (!currentUser || !currentUser.isAdmin) {
      navigate('/');
      return;
    }
    
    // If in edit mode, fetch user data
    if (isEditMode) {
      const fetchUser = async () => {
        try {
          setLoading(true);
          const response = await axiosInstance.get(`/user/${id}`);
          const userData = response;
          
          setFormData({
            username: userData.username || '',
            email: userData.email || '',
            password: '', // Don't populate password field for security
            isAdmin: userData.isAdmin || false,
            isBanned: userData.isBanned || false,
            isVerified: userData.isVerified || false
          });
          
        } catch (err) {
          console.error('Error fetching user:', err);
          setError('Erro ao carregar dados do utilizador.');
        } finally {
          setLoading(false);
        }
      };
      
      fetchUser();
    }
  }, [id, isEditMode, currentUser, navigate]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      if (isEditMode) {
        // Remove password field if empty (don't update password)
        const dataToSubmit = { ...formData };
        if (!dataToSubmit.password) {
          delete dataToSubmit.password;
        }
        
        await axiosInstance.put(`/user/${id}`, dataToSubmit);
      } else {
        await axiosInstance.post('/user', formData);
      }
      
      navigate('/admin-dashboard');
    } catch (err) {
      console.error('Error saving user:', err);
      setError(
        err.response?.data?.message || 
        'Erro ao guardar utilizador. Por favor verifique os dados e tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && isEditMode) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">A carregar...</span>
        </Spinner>
      </Container>
    );
  }
  
  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow-sm">
            <Card.Header as="h5">
              {isEditMode ? 'Editar Utilizador' : 'Criar Novo Utilizador'}
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="formUsername">
                  <Form.Label>Nome de Utilizador</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    Por favor insira um nome de utilizador.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    Por favor insira um email válido.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="formPassword">
                  <Form.Label>
                    {isEditMode ? 'Palavra-passe (deixar em branco para manter a atual)' : 'Palavra-passe'}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!isEditMode}
                    disabled={loading}
                  />
                  {!isEditMode && (
                    <Form.Control.Feedback type="invalid">
                      Por favor insira uma palavra-passe.
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="formIsAdmin">
                  <Form.Check
                    type="checkbox"
                    name="isAdmin"
                    label="Administrador"
                    checked={formData.isAdmin}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="formIsBanned">
                  <Form.Check
                    type="checkbox"
                    name="isBanned"
                    label="Banido"
                    checked={formData.isBanned}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
                
                <Form.Group className="mb-4" controlId="formIsVerified">
                  <Form.Check
                    type="checkbox"
                    name="isVerified"
                    label="Email Verificado"
                    checked={formData.isVerified}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
                
                <div className="d-flex justify-content-between">
                  <Button 
                    variant="secondary" 
                    as={Link} 
                    to="/admin-dashboard"
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit"
                    disabled={loading}
                    style={{ backgroundColor: colors.secondary, color: colors.light, borderColor: colors.secondary }}
                  >
                    {loading ? 'A guardar...' : isEditMode ? 'Atualizar' : 'Criar'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserForm;
